import { remove } from 'lodash';
import { CollectionMenuOption, RawCollection } from '../../types';
import { isOptimisticId } from '../../store/utils';

interface GetCollectionOptionsProps {
    collections: RawCollection[];
    selectedCollectionIds: string[];
    collectionIdsPendingSelect?: string[];
    collectionIdsPendingUnselect?: string[];
    currentCollectionId?: string;
}

/**
 * Converts collections to a format suitable for a collection menu.
 * Sorts collections by currently viewed collection, then selected collections,
 * then unselected collections. The selected/unselected groups are based on the
 * persisted state, not the pending state, so that the options don't jump around
 * while the user is interacting with the menu.
 */
export const getCollectionOptions = ({
    collections,
    selectedCollectionIds,
    collectionIdsPendingSelect = [],
    collectionIdsPendingUnselect = [],
    currentCollectionId,
}: GetCollectionOptionsProps): CollectionMenuOption[] => {
    const selectedCollectionSet = new Set(selectedCollectionIds);
    const collectionIdsPendingSelectSet = new Set(collectionIdsPendingSelect);
    const collectionIdsPendingUnselectSet = new Set(collectionIdsPendingUnselect);

    const initialCollectionOptions = collections?.map((collection) => ({
        id: collection.id,
        name: collection.name,
        isPrivate: collection.ownerType === 'USER',
        checked: selectedCollectionSet.has(collection.id),
    }));

    const [currentCollection] = remove(initialCollectionOptions, (collection) => collection.id === currentCollectionId);
    const checkedCollections = remove(
        initialCollectionOptions,
        (collection) =>
            (collection.checked || collectionIdsPendingUnselectSet.has(collection.id)) &&
            !isOptimisticId(collection.id) &&
            !collectionIdsPendingSelectSet.has(collection.id)
    );
    const uncheckedCollections = initialCollectionOptions; // after mutation

    return [...(currentCollection ? [currentCollection] : []), ...checkedCollections, ...uncheckedCollections];
};
